// src/components/RequestAccess.js
import React, { useState, useEffect, useRef } from 'react';
import { Country, State, City } from 'country-state-city';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { Snackbar, Alert } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import styles from './RequestAccess.module.css';
import {
  validateCompanyId,
  fetchDepartmentsForCompany,
  submitAccessRequest,
} from '../services/requestAccessService';

// ─── Constants ────────────────────────────────────────────────────────────────

const MASTER_ADMIN_EMAIL =
  process.env.REACT_APP_MASTER_ADMIN_EMAIL    || 'masteradmin@company.com';
const MASTER_ADMIN_PASSWORD =
  process.env.REACT_APP_MASTER_ADMIN_PASSWORD || 'Ast@Inf@2025';

const buildInitialForm = (email = '') => ({
  name:           '',
  email,
  company:        '',
  companyId:      '',
  departmentId:   '',
  departmentName: '',
  countryCode:    'IN',
  stateCode:      '',
  city:           '',
  description:    'Need access as a general user',
  address1:       '',
  address2:       '',
  address3:       '',
  pincode:        '',
});

// ─── Component ────────────────────────────────────────────────────────────────

const RequestAccess = () => {
  const [formData, setFormData]           = useState(buildInitialForm());
  const [countries, setCountries]         = useState([]);
  const [states, setStates]               = useState([]);
  const [cities, setCities]               = useState([]);
  const [departments, setDepartments]     = useState([]);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [validatingCompany, setValidatingCompany]   = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [authReady, setAuthReady]         = useState(false);

  // ── Silent master-admin sign-in state ──
  const didMasterLogin = useRef(false);
  const authReadyRef   = useRef(false);

  const navigate = useNavigate();

  // ─── Silent Master-Admin Sign-in (mirrors AdminSignup) ────────────────────

  useEffect(() => {
    const auth = getAuth();

    // If a real user is already logged in, don't hijack their session.
    // We still mark auth ready so form works.
    if (auth.currentUser) {
      authReadyRef.current = true;
      setAuthReady(true);
      return;
    }

    signInWithEmailAndPassword(auth, MASTER_ADMIN_EMAIL, MASTER_ADMIN_PASSWORD)
      .then(() => {
        didMasterLogin.current = true;
        authReadyRef.current   = true;
        setAuthReady(true);
      })
      .catch((err) => {
        console.error('[RequestAccess] Master admin sign-in failed:', err);
        setError('Unable to prepare the request form. Please try again later.');
      });
  }, []);

  // Sign out on unmount ONLY if *we* were the ones who logged in.
  useEffect(() => {
    return () => {
      if (!didMasterLogin.current) return;
      const auth = getAuth();
      if (auth.currentUser?.email === MASTER_ADMIN_EMAIL) {
        signOut(auth).catch(() => {});
      }
    };
  }, []);

  // ─── Safe exit helper ─────────────────────────────────────────────────────

  const safeExit = async (path = '/login') => {
    try {
      const auth = getAuth();
      if (
        didMasterLogin.current &&
        auth.currentUser?.email === MASTER_ADMIN_EMAIL
      ) {
        await signOut(auth);
      }
    } catch (err) {
      console.warn('[RequestAccess] Sign-out failed:', err);
    }
    navigate(path);
  };

  // ─── Wait-for-auth helper (polls ref to avoid stale closure) ──────────────

  const waitForAuth = () =>
    new Promise((resolve) => {
      if (authReadyRef.current) return resolve(true);
      const interval = setInterval(() => {
        if (authReadyRef.current) {
          clearInterval(interval);
          resolve(true);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        resolve(false);
      }, 8000);
    });

  // ─── Geography Setup ──────────────────────────────────────────────────────

  useEffect(() => {
    setCountries(Country.getAllCountries());
    setStates(State.getStatesOfCountry('IN'));
  }, []);

  useEffect(() => {
    setStates(
      formData.countryCode
        ? State.getStatesOfCountry(formData.countryCode) || []
        : []
    );
  }, [formData.countryCode]);

  useEffect(() => {
    if (formData.stateCode && formData.countryCode) {
      setLoadingCities(true);
      const result =
        City.getCitiesOfState(formData.countryCode, formData.stateCode) || [];
      setCities(result.sort((a, b) => a.name.localeCompare(b.name)));
      setLoadingCities(false);
    } else {
      setCities([]);
    }
  }, [formData.stateCode, formData.countryCode]);

  // ─── Department Loader ────────────────────────────────────────────────────
  // Fires whenever a validated company becomes available.
  // Gated on authReady so the Firestore read has credentials.

  useEffect(() => {
    if (!authReady) return;

    if (!formData.companyId || !formData.company) {
      setDepartments([]);
      setFormData(prev => ({ ...prev, departmentId: '', departmentName: '' }));
      return;
    }

    let cancelled = false;
    const loadDepartments = async () => {
      setLoadingDepartments(true);
      const result = await fetchDepartmentsForCompany(formData.companyId);
      if (cancelled) return;
      setDepartments(result);
      setLoadingDepartments(false);
    };

    loadDepartments();
    return () => { cancelled = true; };
  }, [formData.companyId, formData.company, authReady]);

  // ─── Company ID Validation (debounced) ───────────────────────────────────

  const companyDebounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    };
  }, []);

  const handleCompanyIdChange = (value) => {
    const normalized = value.trim().toUpperCase();

    // Immediately clear resolved company while user is still typing
    setFormData(prev => ({
      ...prev,
      companyId:      normalized,
      company:        '',
      departmentId:   '',
      departmentName: '',
    }));
    setDepartments([]);

    if (!normalized) return;

    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);

    companyDebounceRef.current = setTimeout(async () => {
      const ok = await waitForAuth();
      if (!ok) {
        console.warn('[RequestAccess] Auth not ready — skipping company validation');
        return;
      }

      setValidatingCompany(true);
      const result = await validateCompanyId(normalized);
      setValidatingCompany(false);

      // Guard against stale debounce results if user typed again
      setFormData(prev => {
        if (prev.companyId !== normalized) return prev;
        return {
          ...prev,
          company: result.valid ? result.company.name : '',
        };
      });

      if (result.valid) setError('');
    }, 500);
  };

  // ─── Field Change Handler ─────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (error) setError('');

    if (name === 'companyId') {
      handleCompanyIdChange(value);
      return;
    }

    if (name === 'countryCode') {
      setFormData(prev => ({ ...prev, countryCode: value, stateCode: '', city: '' }));
      return;
    }

    if (name === 'stateCode') {
      setFormData(prev => ({ ...prev, stateCode: value, city: '' }));
      return;
    }

    if (name === 'pincode') {
      setFormData(prev => ({ ...prev, pincode: value.replace(/[^0-9]/g, '') }));
      return;
    }

    if (name === 'departmentId') {
      const selected = departments.find(d => d.id === value);
      setFormData(prev => ({
        ...prev,
        departmentId:   value,
        departmentName: selected?.departmentName || selected?.name || '',
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ─── Submit Handler ───────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const ok = await waitForAuth();
    if (!ok) {
      setError('The form is not ready yet. Please try again in a moment.');
      return;
    }

    setSubmitting(true);

    // Full pipeline: validate → re-verify company → persist → email
    // userId is the master-admin uid here (visitor has no account).
    // The service just records it as requestedBy — safe to pass null instead
    // if you don't want to store the master-admin uid. See note below.
    const authUid = getAuth().currentUser?.uid || null;
    const result = await submitAccessRequest(formData, authUid);

    if (!result.success) {
      setError(result.error);
      setSubmitting(false);
      return;
    }

    setSuccess(true);
    setSubmitting(false);
  };

  // ─── Reset ────────────────────────────────────────────────────────────────

  const resetForm = () => {
    setFormData(buildInitialForm());
    setDepartments([]);
    setSuccess(false);
    setError('');
  };

  // ─── Loading Gate ─────────────────────────────────────────────────────────

  if (!authReady && !error) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.requestAccessSpinner}></div>
        <p>Preparing form...</p>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div className={styles.requestAccessModalOverlay}>
        <div className={styles.requestAccessModalContent}>

          <div className={styles.requestAccessModalHeader}>
            <h3>User Sign Up</h3>
            <button
              className={styles.requestAccessCloseButton}
              onClick={() => safeExit('/login')}
              aria-label="Close and return to login"
              type="button"
            >
              <FaTimes />
            </button>
          </div>

          <div className={styles.requestAccessModalBody}>
            {success ? (
              <div
                className={`${styles.requestAccessAlert} ${styles.requestAccessAlertSuccess}`}
                role="alert"
              >
                <div className={styles.requestAccessSuccessContent}>
                  <h4>Request submitted successfully!</h4>
                  <p>
                    Your access request has been received and is being reviewed.
                    You will receive an email confirmation shortly.
                  </p>
                  <p>
                    An administrator will review your request and contact you
                    via email once it's processed.
                  </p>
                </div>
                <div className={styles.requestAccessSuccessActions}>
                  <button
                    type="button"
                    className={styles.requestAccessAlertButton}
                    onClick={resetForm}
                  >
                    Submit Another Request
                  </button>
                  <button
                    type="button"
                    className={styles.requestAccessBtnSecondary}
                    onClick={() => safeExit('/login')}
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div
                  className={styles.requestAccessFormGrid}
                  style={{ gridTemplateColumns: '1fr' }}
                >

                  {/* Full Name */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text" id="name" name="name"
                      value={formData.name} onChange={handleChange}
                      required maxLength="100"
                      placeholder="Enter your full name"
                      disabled={submitting}
                    />
                  </div>

                  {/* Email */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email" id="email" name="email"
                      value={formData.email} onChange={handleChange}
                      required placeholder="Enter your email address"
                      disabled={submitting}
                    />
                    <div className={styles.requestAccessFormHelper}>
                      We'll send a confirmation to this address.
                    </div>
                  </div>

                  {/* Company ID */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="companyId">Company ID *</label>
                    <input
                      type="text" id="companyId" name="companyId"
                      value={formData.companyId} onChange={handleChange}
                      required maxLength="20"
                      placeholder="Enter your company ID (e.g., ABC123)"
                      disabled={submitting}
                      style={{ textTransform: 'uppercase' }}
                    />
                    <div className={styles.requestAccessFormHelper}>
                      Please enter the exact Company ID provided by your organization.
                    </div>
                  </div>

                  {/* Company Name — read only, auto-resolved */}
                  <div className={styles.requestAccessFormGroup}>
                    <label>Company Name</label>
                    <input
                      type="text" name="company"
                      value={formData.company}
                      disabled
                      placeholder="Auto-filled from Company ID"
                    />
                    {formData.companyId && !formData.company && !validatingCompany && (
                      <div
                        className={styles.requestAccessFormHelper}
                        style={{ color: '#dc3545' }}
                      >
                        Not Found
                      </div>
                    )}
                    {formData.companyId && formData.company && !validatingCompany && (
                      <div className={styles.requestAccessCompanyValidated}>
                        ✓ Company found: {formData.company}
                      </div>
                    )}
                    {validatingCompany && (
                      <div className={styles.requestAccessFormHelper}>
                        <span className={styles.requestAccessSpinnerSmall}></span>
                        {' '}Validating company...
                      </div>
                    )}
                  </div>

                  {/* Department */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="departmentId">Department</label>
                    {loadingDepartments ? (
                      <div className={styles.requestAccessFormHelper}>
                        <span className={styles.requestAccessSpinnerSmall}></span>
                        {' '}Loading departments...
                      </div>
                    ) : (
                      <select
                        id="departmentId" name="departmentId"
                        value={formData.departmentId} onChange={handleChange}
                        disabled={submitting || !formData.company || departments.length === 0}
                      >
                        <option value="">
                          {!formData.company
                            ? 'Enter Company ID first'
                            : departments.length === 0
                            ? 'No departments available'
                            : 'Select a department'}
                        </option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>
                            {dept.departmentName || dept.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {!loadingDepartments && formData.company && departments.length === 0 && (
                      <div
                        className={styles.requestAccessFormHelper}
                        style={{ color: '#6c757d' }}
                      >
                        No departments found for this company.
                      </div>
                    )}
                  </div>

                  {/* Address Line 1 */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="address1">Address Line 1 *</label>
                    <input
                      type="text" id="address1" name="address1"
                      value={formData.address1} onChange={handleChange}
                      required maxLength="200"
                      placeholder="Street address, building name, etc."
                      disabled={submitting}
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="address2">Address Line 2</label>
                    <input
                      type="text" id="address2" name="address2"
                      value={formData.address2} onChange={handleChange}
                      maxLength="200"
                      placeholder="Apartment, suite, unit, etc. (optional)"
                      disabled={submitting}
                    />
                  </div>

                  {/* Address Line 3 */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="address3">Address Line 3</label>
                    <input
                      type="text" id="address3" name="address3"
                      value={formData.address3} onChange={handleChange}
                      maxLength="200"
                      placeholder="Additional address information (optional)"
                      disabled={submitting}
                    />
                  </div>

                  {/* Pincode */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="pincode">Pincode *</label>
                    <input
                      type="text" id="pincode" name="pincode"
                      value={formData.pincode} onChange={handleChange}
                      required maxLength="6"
                      placeholder="Enter 6-digit pincode"
                      disabled={submitting}
                    />
                  </div>

                  {/* Country */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="countryCode">Country *</label>
                    <select
                      id="countryCode" name="countryCode"
                      value={formData.countryCode} onChange={handleChange}
                      required disabled={submitting}
                    >
                      <option value="">Select Country</option>
                      {countries.map(c => (
                        <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* State */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="stateCode">State *</label>
                    <select
                      id="stateCode" name="stateCode"
                      value={formData.stateCode} onChange={handleChange}
                      required disabled={!formData.countryCode || submitting}
                    >
                      <option value="">
                        {!formData.countryCode ? 'Select Country First' : 'Select State'}
                      </option>
                      {states.map(s => (
                        <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* City */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="city">City *</label>
                    <select
                      id="city" name="city"
                      value={formData.city} onChange={handleChange}
                      required disabled={!formData.stateCode || loadingCities || submitting}
                    >
                      <option value="">
                        {!formData.stateCode
                          ? 'Select State First'
                          : loadingCities ? 'Loading cities...' : 'Select City'}
                      </option>
                      {cities.map((c, i) => (
                        <option key={`${c.name}-${i}`} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    {loadingCities && (
                      <div className={styles.requestAccessFormHelper}>
                        Loading cities...
                      </div>
                    )}
                  </div>

                  {/* Description — read only */}
                  <div className={styles.requestAccessFormGroup}>
                    <label htmlFor="description">Request Description</label>
                    <div className={styles.requestAccessReadOnlyField}>
                      {formData.description}
                    </div>
                  </div>

                </div>

                <div className={styles.requestAccessModalFooter}>
                  <button
                    type="submit"
                    className={styles.requestAccessBtnPrimary}
                    disabled={submitting || validatingCompany || loadingDepartments}
                  >
                    {submitting ? (
                      <>
                        <span className={styles.requestAccessSpinner} aria-hidden="true"></span>
                        Submitting Request...
                      </>
                    ) : 'Submit Request'}
                  </button>
                  <button
                    type="button"
                    className={styles.requestAccessBtnSecondary}
                    onClick={() => safeExit('/login')}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ── ERROR SNACKBAR ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setError('')}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{
            width: '100%',
            minWidth: 400,
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestAccess;